"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowUpIcon, CheckIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useState, type ReactNode } from "react";

import { isNarrow, prefersReducedMotion, schedule } from "./motion";
import { useSectionInView } from "./section";

/**
 * The hero's phone: a Monday-morning chat where the agent has already acted
 * (followed up on its own, remembered a preference), the owner types one
 * line, and the agent confirms. Plays when the hero scrolls into view and
 * replays on re-entry. The typing is skipped on narrow screens (the message
 * just appears) and the whole thing is shown finished under reduced motion.
 *
 * The figure's aria-label carries the story; the bubbles are aria-hidden so a
 * screen reader hears one sentence rather than a chat replaying. The phone is
 * a light device in both themes, so its colours are literal, not tokens.
 */

/** The face in the mock is a real agent's generated portrait (Jarvis on
 *  prod; 144px WebP of the 1024² original, enough for the 34px circle at 3×
 *  DPR). The name stays "Squidler": the chat is with the product's colleague,
 *  not with one specific agent. A product name, so not translated. */
const AGENT_NAME = "Squidler";
const AGENT_PORTRAIT = "/landing/smb/jarvis.webp";

type Step = "idle" | "sent" | "dots" | "reply" | "card";
const ORDER: readonly Step[] = ["idle", "sent", "dots", "reply", "card"];
const reached = (step: Step, target: Step): boolean => ORDER.indexOf(step) >= ORDER.indexOf(target);

const bubble = "rounded-[18px] px-3.5 py-2.5 text-[14.5px] leading-[1.42] sm:text-[13.5px] sm:max-w-[88%]";
const agentBubble = `${bubble} max-w-[84%] self-start rounded-bl-[5px] border border-[#0A1628]/12 bg-white text-[#0A1628]`;
const appear = "transition-[opacity,transform] duration-500 motion-reduce:transition-none";
const hidden = "translate-y-2.5 opacity-0";

function Tag({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`-mt-1 mb-px ml-1.5 flex items-center gap-1.5 self-start font-mono text-[9.5px] uppercase tracking-[0.12em] text-[#006A98] before:size-[5px] before:rounded-full before:bg-signal before:content-[''] ${className}`}
    >
      {children}
    </span>
  );
}

export function HeroPhone() {
  const { t } = useLingui();
  const inView = useSectionInView();
  const [step, setStep] = useState<Step>("idle");
  const [typed, setTyped] = useState(0);
  const [tapped, setTapped] = useState(false);
  const message = t`Send it. And never Fridays, remember.`;

  // Scheduled, never set synchronously: the steps land on timers and the
  // cleanup (scrolled away, unmounted) puts the chat back to the start.
  useEffect(() => {
    if (!inView) return;
    const { at, every, clear } = schedule();
    if (prefersReducedMotion()) {
      at(0, () => {
        setStep("card");
      });
      return clear;
    }
    const typing = !isNarrow();
    const perChar = 26;
    const typedBy = typing ? 450 + message.length * perChar + 350 : 450;
    if (typing) {
      at(450, () => {
        every(perChar, setTyped, message.length);
      });
      at(typedBy, () => {
        setTapped(true);
      });
    }
    const sentAt = typing ? typedBy + 160 : typedBy;
    at(sentAt, () => {
      setTapped(false);
      setTyped(0);
      setStep("sent");
    });
    at(sentAt + 650, () => {
      setStep("dots");
    });
    at(sentAt + 2150, () => {
      setStep("reply");
    });
    at(sentAt + 2800, () => {
      setStep("card");
    });
    return () => {
      clear();
      setStep("idle");
      setTyped(0);
      setTapped(false);
    };
  }, [inView, message]);

  const draft = message.slice(0, typed);

  return (
    <figure
      aria-label={t`Example chat on a Monday morning. Squidler has drafted a follow-up on a quote nobody answered, and offered two booking requests the days the owner prefers. The owner says send it, and never Fridays. Squidler sends it and keeps Fridays free from then on.`}
      className="relative m-0 h-[470px] w-full max-w-[330px] rounded-[38px] bg-[#0A1628] p-2 shadow-[0_40px_80px_-30px_rgba(10,22,40,0.45),0_0_0_1px_rgba(10,22,40,0.08)] sm:h-[620px] sm:w-[330px] sm:rounded-[46px] sm:p-2.5 lg:h-[660px]"
    >
      {/* The notch */}
      <span aria-hidden="true" className="absolute left-1/2 top-5 z-[2] hidden h-6 w-[84px] -translate-x-1/2 rounded-full bg-[#0A1628] sm:block" />
      <div aria-hidden="true" className="flex h-full flex-col overflow-hidden rounded-[30px] bg-[#FAFAF8] font-sans text-[#0A1628] sm:rounded-[36px]">
        <div className="flex items-center gap-2.5 border-b border-[#0A1628]/12 bg-white px-4 pb-3 pt-3.5 sm:pb-3.5 sm:pt-[50px]">
          {/* A real agent's portrait (Jarvis, our prod PM agent — generated
              brand portrait, pulled from instance_portraits) rather than the
              product logo: a colleague has a face. */}
          <Image
            src={AGENT_PORTRAIT}
            alt=""
            width={34}
            height={34}
            className="size-[34px] shrink-0 rounded-full object-cover"
          />
          <span className="flex flex-col leading-tight">
            <b className="text-[15px] font-semibold">{AGENT_NAME}</b>
            <i className="text-xs not-italic text-[#5E6878]">
              <Trans>Your colleague</Trans>
            </i>
          </span>
        </div>

        {/* overflow-hidden: with justify-end, a thread taller than the frame
            would otherwise spill upward over the header. */}
        <div className="flex min-h-0 flex-1 flex-col justify-end gap-2.5 overflow-hidden px-3.5 pb-3.5 pt-4 sm:gap-2.5">
          <span className="mb-auto hidden self-center text-[11px] font-medium text-[#5E6878] sm:block">
            <Trans>Mon 07:45</Trans>
          </span>
          <p className={agentBubble}>
            <Trans>Morning. The café never answered Thursday&rsquo;s quote, so I drafted a follow-up. Send it?</Trans>
          </p>
          <Tag>
            <Trans>Followed up on its own</Trans>
          </Tag>
          <p className={`${agentBubble} max-sm:hidden`}>
            <Trans>Two booking requests came in overnight. I offered Tuesday and Thursday, like you prefer.</Trans>
          </p>
          <Tag className="max-sm:hidden">
            <Trans>Remembered</Trans>
          </Tag>
          <p
            className={`${bubble} max-w-[84%] self-end rounded-br-[5px] bg-[#0A1628] text-[#FAFAF8] ${appear} ${
              reached(step, "sent") ? "" : hidden
            }`}
          >
            {message}
          </p>
          <div className="relative flex flex-col gap-2">
            {/* Typing dots sit over the reply's slot so nothing jumps when it lands. */}
            <span
              className={`absolute left-0 top-0 flex items-center gap-1 rounded-[18px] rounded-bl-[5px] border border-[#0A1628]/12 bg-white px-3.5 py-[13px] transition-opacity duration-200 ${
                step === "dots" ? "opacity-100" : "opacity-0"
              }`}
            >
              {[0, 150, 300].map((delay) => (
                <i
                  key={delay}
                  className={`size-1.5 rounded-full bg-[#5E6878] opacity-35 ${
                    step === "dots" ? "animate-[sqTyp_1s_ease-in-out_infinite] motion-reduce:animate-none" : ""
                  }`}
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </span>
            <p className={`${agentBubble} ${appear} ${reached(step, "reply") ? "" : hidden}`}>
              <Trans>Sent. No Fridays, ever. I&rsquo;ll keep them free from now on.</Trans>
            </p>
            <div
              className={`flex items-center gap-2 self-start whitespace-nowrap rounded-[18px] border border-[#2ABFAB]/45 bg-white px-3 py-2 text-[12.5px] font-semibold sm:text-[13.5px] ${appear} ${
                reached(step, "card") ? "" : hidden
              }`}
            >
              <span className="grid size-5 shrink-0 place-items-center rounded-full bg-[#2ABFAB] text-white">
                <CheckIcon className="size-3" strokeWidth={2.5} aria-hidden="true" />
              </span>
              <span>
                <Trans>Follow-up sent · Fridays free</Trans>
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-[#0A1628]/12 bg-white px-2.5 pb-3 pt-2 sm:px-3 sm:pb-[18px] sm:pt-2.5">
          <span
            className={`flex h-[38px] min-w-0 flex-1 items-center overflow-hidden whitespace-nowrap rounded-full bg-[#F5F0EB] px-3.5 text-sm ${
              draft ? "justify-end [mask-image:linear-gradient(90deg,transparent_6px,#000_26px)]" : "justify-start text-[#5E6878]"
            }`}
          >
            {draft || <Trans>Message</Trans>}
          </span>
          <span
            className={`grid size-[34px] shrink-0 place-items-center rounded-full bg-signal text-[#FAFAF8] transition-transform duration-200 ${
              tapped ? "scale-[0.86]" : ""
            }`}
          >
            <ArrowUpIcon className="size-3.5" strokeWidth={2.2} aria-hidden="true" />
          </span>
        </div>
      </div>
    </figure>
  );
}
